import { closeX } from './popup.js';
import { $, S, el } from '../state/store.js';
import { renderer } from '../scene/context.js';
import { isOrbitLike, isSiteLike } from '../scene/cameras.js';
import { flatFit, setFlatZoom } from './flat-view.js';
import { setShadowRes } from '../scene/lighting.js';
import { fitStage } from '../scene/renderer.js';
import { emit, requestRender } from '../state/bus.js';
import { APP_VERSION } from '../version.js';
import { renderLeft } from './rail-left.js';
import { checkRow, selectRow, sliderRow } from './controls.js';
import { analyticsAvailable, analyticsConsent, setAnalyticsConsent } from '../services/analytics.js';
import { setSiteZoom, siteFit } from './site-view.js';
import { EXPORT_COST, PRIVACY_URL, canExportVideoFiles, hasLicence } from '../services/limits.js';
import { creditBalance, listCreditHistory, packUntil } from '../services/entitlements.js';
import { motionExpires } from '../services/motion.js';
import { formatDate } from './calendar.js';
import { showBilling } from './billing.js';
import { currentTheme, setTheme } from './theme.js';
import { currentUser, deleteAccount, isAnonymous } from '../services/auth.js';
import { toast } from './toast.js';

/* SETTINGS — workspace/device prefs (theme, UI scale) and render knobs
   (shadow map resolution, export supersampling, gizmo handle size) pulled
   out of the always-visible rail and behind a deliberate "Settings"
   button, same .authwrap/.authcard shell as Templates/Presets/Account.
   Shadow blur lives with the rest of the lighting controls in the Design
   rail instead — see rail-left.js. */

const UISCALE_KEY = 'illusio3d.uiscale';
/* CSS zoom, not transform:scale — zoom keeps layout, ResizeObserver (the
   #stage one in main.js that drives fitStage/fitPhoto) and pointer-event
   coordinates all consistent with the scaled-up rendered size automatically,
   the same way a browser's own Ctrl+/Ctrl- zoom already is. transform would
   need the container's own box compensated by hand to avoid leftover empty
   space, and wouldn't feed ResizeObserver at all. Chrome/Edge only (this
   app's target browsers, per tools/verify.mjs's own channel list) — zoom
   isn't universally supported, but nothing here needs it to be. */
function applyUIScale(z){
  document.documentElement.style.zoom = z;
  try{ localStorage.setItem(UISCALE_KEY, z); }catch(_){}
}
function savedUIScale(){
  try{ return localStorage.getItem(UISCALE_KEY) || '1'; }catch(_){ return '1'; }
}

let overlay = null;
function ensureOverlay() {
  if (overlay) return overlay;
  overlay = el('div', 'authwrap hidden');
  overlay.id = 'settingswrap';
  document.body.appendChild(overlay);
  return overlay;
}
const hide = () => ensureOverlay().classList.add('hidden');
const show = () => ensureOverlay().classList.remove('hidden');

/* A titled group. The panel used to be one flat column of unrelated
   controls — theme sitting next to shadow-map resolution — which made it
   read as a pile rather than a set of decisions. Grouping them means you can
   find the one you came for by scanning four headings instead of nine rows. */
function group(title, parent) {
  const g = el('section', 'setgroup');
  g.appendChild(el('h3', null, title));
  parent.appendChild(g);
  return g;
}

export function showSettings() {
  const w = ensureOverlay();
  w.innerHTML = '';
  const card = el('div', 'authcard authcard--settings');
  card.appendChild(el('h2', null, 'Settings'));
  closeX(card, hide);

  /* Two columns, because the groups are independent — nothing here reads
     top-to-bottom, so a single tall column just meant scrolling past things
     you didn't want. Collapses to one column on a narrow window. */
  const grid = el('div', 'setgrid');
  card.appendChild(grid);

  // ── Appearance ────────────────────────────────────────────────────────
  const look = group('Appearance', grid);
  look.appendChild(selectRow('Theme', currentTheme(), [
    ['light','Light'],['dark','Dark']
  ], v=>{ setTheme(v); }));
  look.appendChild(selectRow('UI scale', savedUIScale(), [
    ['0.8','80%'],['0.9','90%'],['1','100% — default'],['1.1','110%'],['1.25','125%'],['1.5','150%']
  ], v=>{ applyUIScale(v); }));
  look.appendChild(el('p', 'note', 'Scales the whole interface, not just the render — useful on a high-resolution display where everything defaults to reading small. Remembered on this device.'));

  // ── Editing ───────────────────────────────────────────────────────────
  const edit = group('Editing', grid);
  edit.appendChild(sliderRow('Handle size', S.gizmoSize, .5, 3, .05, v=>{S.gizmoSize=v; requestRender();}));
  edit.appendChild(el('p', 'note', 'How large the move, rotate and scale handles are drawn. Bigger handles are easier to grab; smaller ones cover less of what you are looking at.'));
  if(isOrbitLike()){
    edit.appendChild(checkRow('Show screen frame + frustum', S.showHelpers, v=>{S.showHelpers=v; requestRender();}));
    edit.appendChild(el('p', 'note', 'Draws the screen’s outline and the calibrated viewing cone in Orbit and Split, as a working aid — neither ever reaches the export.'));
  }

  // ── View ──────────────────────────────────────────────────────────────
  // Flat and On-site each have their own independent zoom — whichever one
  // is on screen right now, not both. Navigation, not a scene property: it
  // never touches the render or the export, which is also why it left Scene
  // Settings rather than living beside things that do. Scroll (or drag with
  // the middle button) reaches the same state directly on the canvas; this
  // is the numeric/jump-to-a-level way in, next to the other view prefs.
  if(isSiteLike() || S.view==='flat'){
    const zoomIsSite = isSiteLike();
    const view = group('View', grid);
    const badge = el('span', null, Math.round(((zoomIsSite ? S.siteZoom : S.flatZoom)||1)*100)+'%');
    const hd = view.querySelector('h3');
    hd.appendChild(document.createTextNode(' — '));
    hd.appendChild(badge);
    const setBadge = ()=>{ badge.textContent = Math.round(((zoomIsSite ? S.siteZoom : S.flatZoom)||1)*100)+'%'; };
    view.appendChild(sliderRow('Zoom', (zoomIsSite ? S.siteZoom : S.flatZoom)||1, zoomIsSite ? .25 : .5, 8, .05, v=>{
      if(zoomIsSite) setSiteZoom(v); else { setFlatZoom(v); requestRender(); emit('view:zoomed'); }
      setBadge();
    }));
    const zr = el('div','btnrow');
    [['50%',.5],['Fit',1],['200%',2],['400%',4]].forEach(([lbl,z])=>{
      const b = el('button','btn btn--sm btn--gh',lbl);
      b.onclick = ()=>{
        if(zoomIsSite){ if(z===1) siteFit(); else setSiteZoom(z); }
        else { if(z===1) flatFit(); else setFlatZoom(z); requestRender(); emit('view:zoomed'); }
        setBadge();
      };
      zr.appendChild(b);
    });
    view.appendChild(zr);
    view.appendChild(el('p', 'note', `Scroll to zoom where the pointer is${zoomIsSite ? '' : ', or drag with the middle or right button to pan'}. A preview aid — it never touches the render or the export.`));
  }

  // ── Render quality ────────────────────────────────────────────────────
  const q = group('Render quality', grid);
  q.appendChild(el('p', 'note',
    'How carefully the scene is rendered — for the working preview, and for the exported PNG.'));
  q.appendChild(selectRow('Shadow detail', String(S.shadowRes), [
    ['1024','1024 — fastest'],['2048','2048'],['4096','4096 — sharpest']
  ], v=>{setShadowRes(parseInt(v));}));
  q.appendChild(selectRow('Supersample', String(S.ss), [
    ['1','Off — native'],['2','×2'],['3','×3'],['4','×4 — slowest, cleanest']
  ], v=>{ S.ss = parseInt(v); refreshWarn(); renderLeft(); fitStage(); }));
  q.appendChild(checkRow('Preview at export quality', S.previewQuality, v=>{
    S.previewQuality = v; fitStage();
  }));
  const warn = el('p', 'note note--warn hidden');
  q.appendChild(warn);
  q.appendChild(el('p', 'note',
    'On, Orbit and Flat render at the same supersample factor Export uses, so what you see while working already looks like the deliverable — at the cost of some interactivity, especially in Orbit. Off, the preview stays native resolution and only Export supersamples.'));

  function refreshWarn(){
    const gl0 = renderer.getContext();
    const md = gl0.getParameter(gl0.MAX_VIEWPORT_DIMS)[0] || 4096;
    const need = Math.max(S.res.w, S.res.h) * S.ss;
    warn.classList.toggle('hidden', need <= md);
    if (need > md)
      warn.textContent = `×${S.ss} needs ${need} px and this card tops out at ${md}. It’ll be reduced automatically — pick a lower factor for a predictable result.`;
  }
  refreshWarn();

  // ── Licence ───────────────────────────────────────────────────────────
  /* Framed and full width, below the grid rather than inside it. This is the
     only control here that isn't a preference — it's a statement about how
     the app is being used, and the licence terms have to sit with it rather
     than somewhere the person has to go looking. */
  /* The way back from a "no thanks", and the way out of a "yes". A consent
     you cannot revisit is not really a choice — and the bar deliberately
     never asks twice (ui/consent.js), so this is the only route. Shown only
     where analytics could actually run: on localhost, a preview build or an
     automated browser there is nothing to consent to and a switch would be
     claiming otherwise. */
  if(analyticsAvailable || analyticsConsent() !== null){
    const priv = group('Privacy', grid);
    priv.appendChild(checkRow('Share anonymous usage data', analyticsConsent() === 'yes',
      v => { setAnalyticsConsent(v ? 'yes' : 'no'); renderPrivacyNote(); }));
    const note = el('p','note');
    priv.appendChild(note);
    var renderPrivacyNote = () => {
      note.textContent = analyticsConsent() === 'yes'
        ? 'Which features get used — exports run, templates applied. Never what you make. Turning this off stops it from the next reload.'
        : 'Off. Nothing is sent, and the analytics script is never loaded.';
    };
    renderPrivacyNote();
    /* Outside renderPrivacyNote, which rewrites its paragraph's textContent on
       every toggle and would wipe a child link with it. */
    const policy = el('p', 'note');
    const link = el('a', 'licencelink', 'Read the privacy policy');
    link.href = PRIVACY_URL;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    policy.appendChild(link);
    priv.appendChild(policy);
  }

  /* ONLY WHAT THE PERSON HAS. This section used to lay out every kind of access
     — a Licence row reading "None", a paragraph explaining credits, packs and
     licences, a history of nothing — so that everyone could see all of it. It
     now shows the one thing that applies to them, and is named after it:

       licence holder → "Licence": what it is and when it ends.
       everyone else  → "Credits": the balance, a way to buy more, and the
                        history once there is one.

     Nothing here advertises the other kind. The pricing and the terms live in
     the credits dialog (ui/billing.js) and on the site, where somebody looking
     for them will find them. A licence holder is shown no credits at all; if
     the licence lapses the next opening shows the other section. */
  const licensed = hasLicence();
  const lic = group(licensed ? 'Licence' : 'Credits', card);
  lic.classList.add('setgroup--full');
  const box = el('div', 'licencebox');
  const stand = el('div', 'licstand');
  const row = (k, v) => {
    const r = el('p', 'licstand__row');
    r.appendChild(el('span', 'licstand__k', k));
    const val = el('span', 'licstand__v');
    if (typeof v === 'string') val.textContent = v; else val.appendChild(v);
    r.appendChild(val);
    stand.appendChild(r);
    return val;
  };

  if (licensed) {
    row('Type', 'Advanced');
    row('Valid until', motionExpires() ? formatDate(new Date(motionExpires())) : 'No expiry');
  } else {
    /* Said only when true. "Locked" is not something the person has, and what
       unlocks it is the dialog behind Buy credits. */
    /* The date the 12 months run to (0030), or when they ended — the one
       place in the app that says it, since the one-line lock reasons have no
       room. Covers video export, cloud projects and commercial use alike,
       hence "Pack" rather than naming one of the three. */
    const until = packUntil();
    if (canExportVideoFiles().ok && until) row('Pack active until', formatDate(until));
    else if (until) row('Pack ended', formatDate(until));
    const credVal = row('Credits', String(creditBalance()));
    const buy = el('button', 'licencelink', 'Buy credits');
    buy.type = 'button';
    buy.onclick = () => { hide(); showBilling(); };
    credVal.appendChild(document.createTextNode('   '));
    credVal.appendChild(buy);
  }
  box.appendChild(stand);

  if (!licensed) {
    /* HOW YOU GOT THERE. The database's own ledger, which the account can read
       and nothing on a client can write — so it is a record rather than a claim.
       Added only once there is something in it: a heading over "Nothing yet"
       is the same noise as a "None" row. */
    const hist = el('div', 'ledger');
    hist.appendChild(el('h4', 'ledger__h', 'Credit history'));
    const histBody = el('div', 'ledger__body');
    hist.appendChild(histBody);

    const describe = (r) => {
      if (r.reason === 'purchase') return 'Purchased';
      if (r.reason === 'adjustment')
        return r.delta >= 0 ? 'Credits added by Illusio' : 'Credits removed by Illusio';
      /* A year unused, not spent. The nightly sweep (0027) writes this one — a
         credit that just fell out the other end of its own clock, distinct
         from one an export or the owner took. */
      if (r.reason === 'expired') return 'Expired (a year unused)';
      /* The ledger records the amount, not the kind of export, and the amount
         IS the kind — see EXPORT_COST. Anything else is from before exports
         were priced. */
      if (r.delta === -EXPORT_COST.videoPanels) return 'Multi-screen video export';
      if (r.delta === -EXPORT_COST.video) return 'Video export';
      if (r.delta === -EXPORT_COST.onsiteVideo) return 'On-site preview video export';
      if (r.delta === -EXPORT_COST.unwrapPanels) return 'Unwrapped multi-screen video export';
      if (r.delta === -EXPORT_COST.unwrapVideo) return 'Unwrapped video export';
      return 'Export';
    };
    listCreditHistory().then(rows => {
      if (!rows.length) return;
      for (const r of rows) {
        const line = el('div', 'ledger__row');
        line.appendChild(el('span', 'ledger__when', formatDate(new Date(r.created_at))));
        line.appendChild(el('span', 'ledger__what', describe(r)));
        line.appendChild(el('span', 'ledger__delta' + (r.delta < 0 ? ' is-out' : ' is-in'),
          (r.delta > 0 ? '+' : '') + r.delta));
        const bal = el('span', 'ledger__bal', String(r.balance_after));
        bal.title = 'Balance after';
        line.appendChild(bal);
        histBody.appendChild(line);
      }
      box.appendChild(hist);
    }).catch(() => {
      /* A failure is worth saying, unlike an empty list: the person may well
         have a history they cannot see. */
      histBody.appendChild(el('p', 'licencenote licencenote--flush',
        'Couldn’t load your credit history. Sign in and try again.'));
      box.appendChild(hist);
    });
  }

  lic.appendChild(box);

  // ── Account ───────────────────────────────────────────────────────────
  // Shown only for a real, signed-in account — the same test billing.js's
  // own hasAccount() makes. An anonymous session has nothing here worth a
  // button; it already expires on its own (the nightly cleanup, 0014/0019),
  // and there is no email a "your account was deleted" confirmation could
  // even go to.
  const acctUser = currentUser();
  if (acctUser && !isAnonymous()) {
    const acct = group('Account', card);
    acct.classList.add('setgroup--full');
    const del = el('button', 'licencelink licencelink--danger', 'Delete account');
    del.type = 'button';
    del.onclick = async () => {
      if (!confirm(
        `Delete the account at ${acctUser.email}? Your projects, files and `
        + 'presets are deleted with it. This cannot be undone.'
      )) return;
      del.disabled = true;
      del.textContent = 'Deleting…';
      try {
        await deleteAccount();
        hide();
        toast('Your account has been deleted.');
      } catch (err) {
        toast(err.message || 'That didn’t work.', true);
        del.disabled = false;
        del.textContent = 'Delete account';
      }
    };
    /* The consequence first, the action after it — and the link inside its
       own paragraph, so it is inline and only as wide as its own text. As a
       direct child of the group it stretched to the full row, which made the
       whole line a hit target: the opposite of what a link was for. */
    acct.appendChild(el('p', 'note',
      'Your projects, files and presets are deleted with it. Purchase records '
      + 'are kept, as required for accounting, and a Team licence — tied to '
      + 'your email rather than to this account — is unaffected.'));
    const delRow = el('p', 'note');
    delRow.appendChild(del);
    acct.appendChild(delRow);
  }

  // ── Footer ────────────────────────────────────────────────────────────
  const { commit, branch, dirty } = APP_VERSION;
  const foot = el('div', 'authrow authrow--split');
  foot.appendChild(el('p', 'authnote', `Build ${commit}${dirty ? '+' : ''} (${branch})`));
  const close = el('button', 'btn btn--gh', 'Close');
  close.onclick = hide;
  foot.appendChild(close);
  card.appendChild(foot);

  w.appendChild(card);
  show();
}

export function initSettings() {
  const btn = $('#btnSettings');
  if (btn) btn.onclick = showSettings;
}
